import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import { formatDateShort } from "@/lib/format";
import type { BudgetLineRow } from "@/hooks/use-project-detail";

interface Props {
  budgetLine: BudgetLineRow;
  onAssign?: () => void;
  disabled?: boolean;
  /** Hide the "+ Tildel" CTA — used when the row is expanded so the action-bar button is the single trigger. */
  hideAssignButton?: boolean;
}

/**
 * "Tildelt til"-celle som matcher designet (Variant 3): kalender-ikon
 * med tidligste deadline (hvis satt på noen tildeling), pluss enten en
 * avatar-stack med tildelte personer eller en stiplet "+ Tildel"-knapp
 * når oppgaven ikke har noen tildelinger.
 */
export function BudgetAssignedToCell({ budgetLine: bl, onAssign, disabled, hideAssignButton }: Props) {
  const earliestDeadline = bl.assignments
    .map((a) => a.estimated_deadline)
    .filter((d): d is string => !!d)
    .sort()[0];

  const hasAssignments = bl.assignments.length > 0;

  return (
    <div className="flex items-center justify-center gap-3 py-1">
      {earliestDeadline && (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
          <SrIcon name="calendar" className="w-3 h-3" />
          {formatDateShort(earliestDeadline)}
        </span>
      )}

      {hasAssignments ? (
        <div className="flex -space-x-1.5">
          {bl.assignments.slice(0, 4).map((a) => (
            <Avatar
              key={a.user_id}
              name={a.user?.full_name ?? "?"}
              avatarUrl={a.user?.avatar_url}
              size="sm"
              className="border-2 border-background"
            />
          ))}
          {bl.assignments.length > 4 && (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-[10px] font-medium border-2 border-background">
              +{bl.assignments.length - 4}
            </span>
          )}
        </div>
      ) : hideAssignButton ? (
        <span className="text-xs text-muted-foreground">—</span>
      ) : (
        <button
          type="button"
          onClick={onAssign}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed text-xs font-medium",
            disabled
              ? "border-foreground/20 text-muted-foreground/50 cursor-not-allowed"
              : "border-primary text-primary hover:bg-primary/5",
          )}
        >
          <SrIcon name="plus" className="w-3 h-3" />
          Tildel
        </button>
      )}
    </div>
  );
}

